import axios, {
  type AxiosError,
  type AxiosRequestConfig,
  type InternalAxiosRequestConfig,
} from 'axios'
import i18n from '@/i18n'
import { showNotification } from '@/utils/notification'

// Standard API error response format
export interface APIError {
  code: string
  message: string
  details?: unknown
}

// Map backend error messages to i18n keys
const errorMessageToI18nKey: Record<string, string> = {
  // Unique constraint violations
  'this demand is already connected to this supply': 'apiError.demandAlreadyConnected',
  'email already exists': 'apiError.emailExists',
  'code already exists': 'apiError.codeExists',
  'name already exists': 'apiError.nameExists',
  'a record with this value already exists': 'apiError.recordExists',
  'deal code already exists': 'apiError.codeExists',

  // Foreign key violations
  'invalid tenant': 'apiError.invalidTenant',
  'invalid supply': 'apiError.invalidSupply',
  'invalid demand': 'apiError.invalidDemand',
  'referenced record does not exist': 'apiError.notFound',

  // Check constraint violations
  'safety_level must be strict, standard, or relaxed': 'apiError.invalidSafetyLevel',
  'invalid status value': 'apiError.invalidStatus',
  'invalid type value': 'apiError.invalidType',
  'value violates constraint': 'apiError.constraintViolation',
  'rev_share must be between 0 and 100': 'apiError.revShareRange',
  'pacing_type must be asap, even, or front_loaded': 'apiError.invalidPacingType',

  // Not null violations
  'a required field is missing': 'apiError.requiredFieldMissing',
}

// Translate backend error message to localized message
function translateErrorMessage(message: string): string {
  const lowerMessage = message.toLowerCase()

  // Check for exact match first
  for (const [key, i18nKey] of Object.entries(errorMessageToI18nKey)) {
    if (lowerMessage === key.toLowerCase()) {
      return i18n.t(i18nKey)
    }
  }

  // Check for partial match (e.g., "xxx is required")
  if (lowerMessage.endsWith(' is required')) {
    const fieldName = message.replace(/ is required$/i, '')
    return `${fieldName} ${i18n.t('validation.required').toLowerCase()}`
  }

  // Return original message if no translation found
  return message
}

// Helper to extract error message from API response
export function getAPIErrorMessage(error: unknown, fallback = 'An error occurred'): string {
  const err = error as { response?: { data?: { error?: APIError } } }
  return err.response?.data?.error?.message || fallback
}

// Public API origin for user-facing URLs (API guide, sellers.json, etc.)
// Derived from VITE_API_BASE_URL by stripping the path (e.g. https://api.orartb.com/api/v1 → https://api.orartb.com)
export const API_PUBLIC_ORIGIN = (() => {
  const base = import.meta.env.VITE_API_BASE_URL
  if (base?.startsWith('http')) {
    try {
      return new URL(base).origin
    } catch {
      /* fall through */
    }
  }
  return window.location.origin
})()

export const apiClient = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL || '/api/v1',
  // 30s default — analytics reports (multi-dim GROUP BY across
  // 100M+ rows) legitimately need 5-10s server-side; 10s left no
  // headroom for serialization/network. Most requests still
  // complete in <1s; longer timeout only affects worst case.
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
})

// Request interceptor
apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token')
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }
    return config
  },
  (error) => {
    return Promise.reject(error)
  },
)

// Token refresh state — prevents multiple concurrent refresh requests
let isRefreshing = false
let refreshSubscribers: ((token: string) => void)[] = []

function onTokenRefreshed(token: string) {
  for (const cb of refreshSubscribers) cb(token)
  refreshSubscribers = []
}

function addRefreshSubscriber(cb: (token: string) => void) {
  refreshSubscribers.push(cb)
}

function clearAuthAndRedirect() {
  localStorage.removeItem('token')
  localStorage.removeItem('refresh_token')
  localStorage.removeItem('user')
  localStorage.removeItem('auth-storage')
  const t = i18n.t.bind(i18n)
  showNotification.warning(t('apiError.title'), t('apiError.unauthorized'))
  window.location.href = '/login'
}

// Response interceptor
apiClient.interceptors.response.use(
  (response) => response.data,
  async (error: AxiosError<{ error?: { code?: string; message?: string }; message?: string }>) => {
    const t = i18n.t.bind(i18n)
    const errorTitle = t('apiError.title')
    const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean }

    if (error.response) {
      const { status, data } = error.response
      const serverMessage = data?.error?.message || data?.message
      const errorCode = data?.error?.code

      switch (status) {
        case 401: {
          if (window.location.pathname.includes('/login')) {
            showNotification.error(errorTitle, serverMessage || t('apiError.unauthorized'))
            break
          }

          // Try auto-refresh with refresh token
          const refreshToken = localStorage.getItem('refresh_token')
          if (!refreshToken || originalRequest._retry) {
            clearAuthAndRedirect()
            break
          }

          if (isRefreshing) {
            // Another request is already refreshing — queue this one
            return new Promise((resolve) => {
              addRefreshSubscriber((newToken: string) => {
                originalRequest.headers.Authorization = `Bearer ${newToken}`
                resolve(apiClient(originalRequest))
              })
            })
          }

          originalRequest._retry = true
          isRefreshing = true

          try {
            const res = await axios.post(`${apiClient.defaults.baseURL}/auth/refresh`, {
              refresh_token: refreshToken,
            })
            const newToken = res.data?.data?.token || res.data?.token
            const newRefresh = res.data?.data?.refresh_token || res.data?.refresh_token
            if (newToken) {
              localStorage.setItem('token', newToken)
              if (newRefresh) localStorage.setItem('refresh_token', newRefresh)
              originalRequest.headers.Authorization = `Bearer ${newToken}`
              onTokenRefreshed(newToken)
              return apiClient(originalRequest)
            }
            clearAuthAndRedirect()
          } catch {
            clearAuthAndRedirect()
          } finally {
            isRefreshing = false
          }
          break
        }
        case 403:
          showNotification.warning(errorTitle, t('apiError.forbidden'))
          break
        case 404:
          showNotification.warning(errorTitle, t('apiError.notFound'))
          break
        case 409:
          showNotification.warning(
            errorTitle,
            serverMessage ? translateErrorMessage(serverMessage) : t('apiError.conflict'),
          )
          break
        case 400:
          // Validation error - show server message if available
          if (errorCode === 'VALIDATION_ERROR' && serverMessage) {
            showNotification.warning(t('apiError.validation'), translateErrorMessage(serverMessage))
          } else {
            showNotification.warning(
              errorTitle,
              serverMessage ? translateErrorMessage(serverMessage) : t('apiError.unknownError'),
            )
          }
          break
        case 500:
          showNotification.error(errorTitle, t('apiError.serverError'))
          break
        default:
          showNotification.error(
            errorTitle,
            serverMessage ? translateErrorMessage(serverMessage) : t('apiError.unknownError'),
          )
      }
    } else if (error.request) {
      showNotification.error(errorTitle, t('apiError.networkError'))
    }

    return Promise.reject(error)
  },
)

// Generic request function with type safety
export async function request<T>(config: AxiosRequestConfig): Promise<T> {
  return apiClient.request<unknown, T>(config)
}

// Convenience methods
export const api = {
  get: <T>(url: string, config?: AxiosRequestConfig) =>
    request<T>({ ...config, method: 'GET', url }),

  post: <T>(url: string, data?: unknown, config?: AxiosRequestConfig) =>
    request<T>({ ...config, method: 'POST', url, data }),

  put: <T>(url: string, data?: unknown, config?: AxiosRequestConfig) =>
    request<T>({ ...config, method: 'PUT', url, data }),

  patch: <T>(url: string, data?: unknown, config?: AxiosRequestConfig) =>
    request<T>({ ...config, method: 'PATCH', url, data }),

  delete: <T>(url: string, config?: AxiosRequestConfig) =>
    request<T>({ ...config, method: 'DELETE', url }),
}
